import { Fragment, useMemo, useRef, useState, useCallback } from "react";
import { sankey, sankeyLinkHorizontal, sankeyLeft } from "d3-sankey";
import type { SankeyNode, SankeyLink, SankeyGraph } from "d3-sankey";
import useAdminQuery from "../../lib/queries/useAdminQuery";
import { Topbar } from "../../components/AppShell";
import { FiltersBar, filtersToPills } from "../../lib/funnel/filters";
import type { Filters } from "../../lib/funnel/filters";
import { groupCountry, fmtN } from "../../lib/funnel/helpers";
import { SQL, mapAttrRows, WORD_SQL, mapWordRows } from "./sql";
import type { AttrRow, WordRow } from "./sql";
import { WordCloud } from "../../components/WordCloud";
import { LoadingPanel } from "../../components/LoadingPanel";
import { SectionCardHeader } from "../../components/ChartCard";
import { FilterPills } from "../../components/FilterPills";
import type { FilterPillSpec } from "../../components/FilterPills";

const PRIMARY  = "#2F7A8A";
const VIOLET   = "#5F6FA0";
const MUTED    = "#A5A196";
const ORANGE   = "#E0A84C";
const FG1      = "#1F1E1A";
const FG3      = "#7A766C";

const ATTR_COLORS: Record<string, string> = {
  "Paid Social (Meta)": "#ab8bd3",
  "Search & Press":     "#6681fe",
  "Referral":           "#7ad068",
  "Workplace":          "#F59F00",
  "Conference":         "#f66ef1",
  "LinkedIn":           "#385bfc",
};
const ATTR_OTHER = "Other / Unknown";

function attributionGroup(cat: string): string {
  switch (cat) {
    case "meta":           return "Paid Social (Meta)";
    case "google":
    case "organic_search":
    case "press":          return "Search & Press";
    case "referral":       return "Referral";
    case "workplace":      return "Workplace";
    case "conference":     return "Conference";
    case "linkedin":       return "LinkedIn";
    default:               return ATTR_OTHER;
  }
}

type Outcome = "Plus" | "Pro" | "Free (active)" | "Free (inactive)";
const OUTCOMES: Outcome[] = ["Free (inactive)", "Free (active)", "Plus", "Pro"];
const OUTCOME_COLORS: Record<Outcome, string> = {
  "Plus":           PRIMARY,
  "Pro":            "#52b788",
  "Free (active)":  "#6f7681",
  "Free (inactive)":"#c1c7d0",
};
const OUTCOME_LABELS: Record<Outcome, string> = {
  "Plus":           "Plus",
  "Pro":            "Pro",
  "Free (active)":  "Free (active)",
  "Free (inactive)":"Free (inactive)",
};
const HEATMAP_COLORS: Record<Outcome, [number, number, number]> = {
  "Plus":           [47, 122, 138],
  "Pro":            [82, 183, 136],
  "Free (active)":  [111, 118, 129],
  "Free (inactive)":[160, 168, 180],
};


function applyWordFilters(rows: WordRow[], f: Filters): WordRow[] {
  const monthSet = new Set(f.months);
  const countrySet = new Set(f.countries);
  const roleSet = new Set(f.roles);
  const sizeSet = new Set(f.companySizes);
  return rows.filter((r) => {
    if (f.excludeSupport && r.user_role === "support") return false;
    if (monthSet.size > 0 && !monthSet.has(r.created_month)) return false;
    if (countrySet.size > 0 && !countrySet.has(groupCountry(r.country_code))) return false;
    if (roleSet.size > 0 && !roleSet.has(r.user_role ?? "unknown")) return false;
    if (sizeSet.size > 0 && !sizeSet.has(r.company_size_bucket)) return false;
    return true;
  });
}


function applyAttrFilters(rows: AttrRow[], f: Filters): AttrRow[] {
  const monthSet = new Set(f.months);
  const countrySet = new Set(f.countries);
  const roleSet = new Set(f.roles);
  const sizeSet = new Set(f.companySizes);
  return rows.filter((r) => {
    if (f.excludeSupport && r.user_role === "support") return false;
    if (monthSet.size > 0 && !monthSet.has(r.created_month)) return false;
    if (countrySet.size > 0 && !countrySet.has(groupCountry(r.country_code))) return false;
    if (roleSet.size > 0 && !roleSet.has(r.user_role ?? "unknown")) return false;
    if (sizeSet.size > 0 && !sizeSet.has(r.company_size_bucket)) return false;
    return true;
  });
}

type SankeyVariant = "demo" | "self";
type NodeKind = "attr" | "center" | "outcome";
type NodeDatum = { name: string; kind: NodeKind };
type LinkDatum = { value: number };
type D3Node = SankeyNode<NodeDatum, LinkDatum>;
type D3Link = SankeyLink<NodeDatum, LinkDatum>;
type D3Graph = SankeyGraph<NodeDatum, LinkDatum>;

function buildGraph(rows: AttrRow[]): { graph: D3Graph; total: number; converted: number; center: string } {
  const variant: SankeyVariant = rows[0]?.had_demo ? "demo" : "self";
  const center = variant === "demo" ? "Had demo" : "Self-serve";

  const attrCounts = new Map<string, number>();
  const outcomeCounts = new Map<Outcome, number>();
  let total = 0;
  let converted = 0;

  for (const r of rows) {
    const a = attributionGroup(r.attribution_category);
    const o = r.outcome as Outcome;
    attrCounts.set(a, (attrCounts.get(a) ?? 0) + r.user_count);
    outcomeCounts.set(o, (outcomeCounts.get(o) ?? 0) + r.user_count);
    total += r.user_count;
    if (o === "Pro" || o === "Plus") converted += r.user_count;
  }

  const attrOrder = Array.from(attrCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([k]) => k);

  const nodes: NodeDatum[] = [];
  const idx = new Map<string, number>();
  attrOrder.forEach((n) => { idx.set(n, nodes.length); nodes.push({ name: n, kind: "attr" }); });
  idx.set(center, nodes.length);
  nodes.push({ name: center, kind: "center" });
  OUTCOMES.forEach((o) => {
    if ((outcomeCounts.get(o) ?? 0) > 0) {
      idx.set(o, nodes.length);
      nodes.push({ name: o, kind: "outcome" });
    }
  });

  const centerIdx = idx.get(center) as number;
  const links: LinkDatum[] = [];
  for (const [a, v] of attrCounts) {
    const s = idx.get(a);
    if (s == null) continue;
    links.push({ source: s as unknown as NodeDatum, target: centerIdx as unknown as NodeDatum, value: v } as unknown as LinkDatum);
  }
  for (const o of OUTCOMES) {
    const v = outcomeCounts.get(o) ?? 0;
    if (v === 0) continue;
    const t = idx.get(o);
    if (t == null) continue;
    links.push({ source: centerIdx as unknown as NodeDatum, target: t as unknown as NodeDatum, value: v } as unknown as LinkDatum);
  }

  const graph = { nodes, links } as unknown as D3Graph;
  return { graph, total, converted, center };
}

const HEIGHT = 500;
const MARGIN = { top: 16, right: 140, bottom: 16, left: 150 };

function nodeColor(node: D3Node, variant: SankeyVariant): string {
  if (node.kind === "center") return variant === "demo" ? ORANGE : "#4a5468";
  if (node.kind === "outcome") return OUTCOME_COLORS[node.name as Outcome] ?? MUTED;
  if (node.name === ATTR_OTHER) return MUTED;
  return ATTR_COLORS[node.name] ?? VIOLET;
}

type TooltipState = { x: number; y: number; label: string; value: number } | null;

function SankeyCard({ rows, variant, pagePills }: { rows: AttrRow[]; variant: SankeyVariant; pagePills: FilterPillSpec[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipState>(null);

  const heading = variant === "demo" ? "Had demo" : "Self-serve";
  const headingColor = variant === "demo" ? ORANGE : "#4a5468";

  const tilePills = useMemo<FilterPillSpec[]>(
    () => [...pagePills, { kind: "mode", label: variant === "demo" ? "Had demo path" : "Self-serve path" }],
    [pagePills, variant],
  );

  const result = useMemo(() => {
    if (rows.length === 0) return null;
    const { graph, total, converted, center: _c } = buildGraph(rows);
    const innerW = 560 - MARGIN.left - MARGIN.right;
    const innerH = HEIGHT - MARGIN.top - MARGIN.bottom;
    const gen = sankey<NodeDatum, LinkDatum>()
      .nodeWidth(14)
      .nodePadding(6)
      .nodeAlign(sankeyLeft)
      .nodeSort(null)
      .iterations(32)
      .extent([[0, 0], [innerW, innerH]]);
    return { layout: gen(graph), total, converted };
  }, [rows]);

  const handleLinkEnter = useCallback((e: React.MouseEvent<SVGPathElement>, link: D3Link) => {
    const src = link.source as D3Node;
    const tgt = link.target as D3Node;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTooltip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      label: `${src.name} → ${tgt.name}`,
      value: link.value,
    });
  }, []);

  const handleLinkLeave = useCallback(() => setTooltip(null), []);

  if (!result || result.total === 0) {
    return (
      <div className="chart-tile">
        <div style={{ textAlign: "center", marginBottom: 6 }}>
          <div style={{ color: headingColor, fontWeight: 700, fontSize: 13 }}>{heading}</div>
          <div style={{ color: FG3, fontSize: 11 }}>No users</div>
        </div>
        <div style={{ marginBottom: 8 }}>
          <FilterPills pills={tilePills} />
        </div>
        <div className="empty" style={{ padding: 24 }}>No data</div>
      </div>
    );
  }

  const { layout, total, converted } = result;
  const convPct = total > 0 ? ((converted / total) * 100).toFixed(1) : "0.0";
  const innerW = 560 - MARGIN.left - MARGIN.right;

  return (
    <div className="chart-tile" ref={containerRef} style={{ position: "relative" }}>
      <div style={{ textAlign: "center", marginBottom: 4 }}>
        <div style={{ color: headingColor, fontWeight: 700, fontSize: 13 }}>{heading}</div>
        <div style={{ color: FG3, fontSize: 11 }}>
          n={total} · {convPct}% converted
        </div>
      </div>
      <div style={{ marginBottom: 8, display: "flex", justifyContent: "center" }}>
        <FilterPills pills={tilePills} />
      </div>
      <svg
        viewBox={`0 0 560 ${HEIGHT}`}
        width="100%"
        style={{ display: "block" }}
        aria-label={`${heading} attribution Sankey diagram`}
      >
        <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
          {layout.links.map((link, i) => {
            const path = sankeyLinkHorizontal()(link as unknown as Parameters<ReturnType<typeof sankeyLinkHorizontal>>[0]);
            if (!path) return null;
            const src = link.source as D3Node;
            const tgt = link.target as D3Node;
            const col = tgt.kind === "outcome"
              ? (OUTCOME_COLORS[tgt.name as Outcome] ?? MUTED)
              : nodeColor(src, variant);
            return (
              <path
                key={i}
                d={path}
                fill="none"
                stroke={col}
                strokeWidth={link.width ?? 1}
                strokeOpacity={0.45}
                onMouseMove={(e) => handleLinkEnter(e, link)}
                onMouseLeave={handleLinkLeave}
                style={{ cursor: "default" }}
              />
            );
          })}
          {layout.nodes.map((node, i) => {
            const x0 = node.x0 ?? 0;
            const x1 = node.x1 ?? 0;
            const y0 = node.y0 ?? 0;
            const y1 = node.y1 ?? 0;
            const w = x1 - x0;
            const h = y1 - y0;
            const fill = nodeColor(node, variant);
            const labelOnLeft = node.kind === "attr";
            const pct = total > 0 ? ((node.value ?? 0) / total) * 100 : 0;
            return (
              <g key={i}>
                <rect x={x0} y={y0} width={w} height={h} fill={fill} fillOpacity={0.92} rx={2} />
                {node.kind !== "center" && (
                  <>
                    <text
                      x={labelOnLeft ? x0 - 8 : x1 + 8}
                      y={y0 + h / 2 - 1}
                      textAnchor={labelOnLeft ? "end" : "start"}
                      dominantBaseline="middle"
                      fontSize={11}
                      fill={FG1}
                      fontWeight={600}
                    >
                      {node.name}
                    </text>
                    <text
                      x={labelOnLeft ? x0 - 8 : x1 + 8}
                      y={y0 + h / 2 + 12}
                      textAnchor={labelOnLeft ? "end" : "start"}
                      dominantBaseline="middle"
                      fontSize={10}
                      fill={FG3}
                    >
                      {node.value} · {pct.toFixed(0)}%
                    </text>
                  </>
                )}
              </g>
            );
          })}
          <rect x={innerW} y={0} width={1} height={1} fill="none" />
        </g>
      </svg>
      {tooltip && (
        <div
          style={{
            position: "absolute",
            left: tooltip.x + 12,
            top: tooltip.y - 28,
            background: "var(--bg-elevated, #fff)",
            border: "1px solid var(--border-default, #e0e0e0)",
            borderRadius: 6,
            padding: "4px 8px",
            fontSize: 12,
            pointerEvents: "none",
            whiteSpace: "nowrap",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            color: FG1,
          }}
        >
          <strong>{tooltip.label}</strong>: {tooltip.value.toLocaleString()}
        </div>
      )}
    </div>
  );
}

const CHANNEL_GROUPS: { name: string; channels: string[] }[] = [
  { name: "Paid",               channels: ["Paid Social (Meta)", "LinkedIn"] },
  { name: "Organic & Referral", channels: ["Search & Press", "Referral", "Conference", "Workplace"] },
];
const GROUPED_CHANNELS = new Set(CHANNEL_GROUPS.flatMap((g) => g.channels));

const CHANNEL_ICONS: Record<string, string> = {
  "Paid Social (Meta)":  "ti-brand-meta",
  "LinkedIn":            "ti-brand-linkedin",
  "Search & Press":      "ti-news",
  "Referral":            "ti-speakerphone",
  "Conference":          "ti-tie",
  "Workplace":           "ti-building",
  "Other / Unknown":     "ti-question-mark",
  "Paid":                "ti-credit-card",
  "Organic & Referral":  "ti-leaf",
};

const OUTCOME_DESCRIPTIONS: Record<Outcome, string> = {
  "Free (inactive)": "Signed up but no transcripts or summaries yet",
  "Free (active)":   "Free plan with at least one transcript or summary",
  "Plus":            "Upgraded to Plus plan",
  "Pro":             "Upgraded to Pro plan",
};

type HeatmapTooltip = {
  x: number; y: number;
  channel: string; outcome: Outcome; count: number; pct: number;
} | null;

type HeatViewMode = "heat" | "bar";

function HeatmapCard({ rows, pagePills }: { rows: AttrRow[]; pagePills: FilterPillSpec[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [tooltip, setTooltip] = useState<HeatmapTooltip>(null);
  const [headerTooltip, setHeaderTooltip] = useState<{ x: number; y: number; outcome: Outcome } | null>(null);
  const [viewMode, setViewMode] = useState<HeatViewMode>("heat");

  const channelData = useMemo(() => {
    const attrMap = new Map<string, Map<string, number>>();
    for (const r of rows) {
      const attr = attributionGroup(r.attribution_category);
      if (!attrMap.has(attr)) attrMap.set(attr, new Map());
      const inner = attrMap.get(attr)!;
      inner.set(r.outcome, (inner.get(r.outcome) ?? 0) + r.user_count);
    }
    return Array.from(attrMap.entries()).map(([attr, counts]) => {
      const total = Array.from(counts.values()).reduce((s, v) => s + v, 0);
      return { attr, counts, total };
    });
  }, [rows]);

  const colTotals = useMemo(() => {
    const totals = {} as Record<Outcome, number>;
    for (const o of OUTCOMES) {
      totals[o] = channelData.reduce((s, row) => s + (row.counts.get(o) ?? 0), 0);
    }
    return totals;
  }, [channelData]);

  const colMax = useMemo(() => {
    const maxes = {} as Record<Outcome, number>;
    for (const o of OUTCOMES) {
      const ct = colTotals[o] ?? 0;
      let max = 0;
      for (const row of channelData) {
        const v = row.counts.get(o) ?? 0;
        const pct = ct > 0 ? v / ct : 0;
        if (pct > max) max = pct;
      }
      maxes[o] = max;
    }
    return maxes;
  }, [channelData, colTotals]);

  const groupAggregates = useMemo(() => {
    const byName = new Map(channelData.map((d) => [d.attr, d]));
    return CHANNEL_GROUPS.map((g) => {
      const counts = new Map<string, number>();
      let total = 0;
      for (const ch of g.channels) {
        const d = byName.get(ch);
        if (!d) continue;
        total += d.total;
        for (const [o, v] of d.counts) counts.set(o, (counts.get(o) ?? 0) + v);
      }
      return { name: g.name, counts, total };
    });
  }, [channelData]);

  const channelByName = useMemo(() => new Map(channelData.map((d) => [d.attr, d])), [channelData]);

  const standaloneChannels = useMemo(
    () => channelData.filter((d) => !GROUPED_CHANNELS.has(d.attr)).sort((a, b) => b.total - a.total),
    [channelData],
  );

  function toggleGroup(name: string) {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  }

  function handleCellMove(e: React.MouseEvent, channel: string, outcome: Outcome, count: number, pct: number) {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, channel, outcome, count, pct });
  }

  function renderCell(channel: string, outcome: Outcome, count: number, _rowTotal: number, indented = false) {
    const ct = colTotals[outcome] ?? 0;
    const pct = ct > 0 ? count / ct : 0;
    const intensity = (colMax[outcome] ?? 0) > 0 ? pct / (colMax[outcome] ?? 1) : 0;
    const [r, g, b] = HEATMAP_COLORS[outcome];

    const cellContent = pct > 0 ? (
      viewMode === "heat" ? (
        <div style={{
          background: `rgba(${r},${g},${b},${0.12 + intensity * 0.72})`,
          borderRadius: 6, padding: "5px 6px",
          color: intensity < 0.45 ? FG1 : "#fff", fontWeight: 600, fontSize: 12,
          display: "block", width: "100%", boxSizing: "border-box", cursor: "default",
        }}>
          {`${(pct * 100).toFixed(0)}%`}
        </div>
      ) : (
        <div style={{ position: "relative", height: 26, borderRadius: 5, overflow: "hidden", background: "var(--bg-2)" }}>
          <div style={{
            position: "absolute", left: 0, top: 0, bottom: 0,
            width: `${intensity * 100}%`,
            background: `rgba(${r},${g},${b},0.65)`,
            borderRadius: 5,
          }} />
          <span style={{
            position: "absolute", left: 0, right: 0, top: 0, bottom: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 11, fontWeight: 600,
            color: intensity > 0.55 ? "#fff" : FG1,
          }}>
            {`${(pct * 100).toFixed(0)}%`}
          </span>
        </div>
      )
    ) : (
      <span style={{ color: MUTED, fontSize: 12 }}>-</span>
    );

    return (
      <td
        key={outcome}
        style={{ padding: "3px 4px", textAlign: "center", opacity: indented ? 0.85 : 1 }}
        onMouseMove={(e) => handleCellMove(e, channel, outcome, count, pct)}
        onMouseLeave={() => setTooltip(null)}
      >
        {cellContent}
      </td>
    );
  }

  function renderTotalCell(total: number, grandTotal: number, indented = false) {
    const pct = grandTotal > 0 ? total / grandTotal : 0;
    return (
      <td style={{ padding: "3px 4px", textAlign: "right", opacity: indented ? 0.85 : 1, whiteSpace: "nowrap" }}>
        <span style={{ fontSize: 12, color: FG3 }}>
          {total.toLocaleString()}
          <span style={{ color: MUTED, marginLeft: 4 }}>({(pct * 100).toFixed(0)}%)</span>
        </span>
      </td>
    );
  }

  if (channelData.length === 0) return null;

  const grandTotal = channelData.reduce((s, d) => s + d.total, 0);

  const heatmapPills: FilterPillSpec[] = [
    ...pagePills,
    { kind: "mode", label: viewMode === "heat" ? "Heat view" : "Bar view" },
  ];

  return (
    <div className="card" style={{ marginTop: 16, position: "relative" }} ref={containerRef}>
      <SectionCardHeader
        title="Conversion rates per attribution channel"
        subtitle="Percentages show each channel's share of that outcome column. Intensity is relative to the top channel per outcome. Click a group to expand individual channels."
        filterPills={heatmapPills}
        headerExtra={
          <div className="btn-group" style={{ flexShrink: 0 }}>
            <button
              className={`btn btn-sm${viewMode === "heat" ? " is-active" : ""}`}
              onClick={() => setViewMode("heat")}
              style={{ fontWeight: viewMode === "heat" ? 600 : 400 }}
            >
              <i className="ti ti-layout-grid" style={{ marginRight: 4 }} />Heat
            </button>
            <button
              className={`btn btn-sm${viewMode === "bar" ? " is-active" : ""}`}
              onClick={() => setViewMode("bar")}
              style={{ fontWeight: viewMode === "bar" ? 600 : 400 }}
            >
              <i className="ti ti-chart-bar" style={{ marginRight: 4 }} />Bar
            </button>
          </div>
        }
      />
      <div className="card-body">
        <table style={{ borderCollapse: "collapse", width: "100%", tableLayout: "fixed", fontSize: 13 }}>
          <colgroup>
            <col style={{ width: "20%" }} />
            {OUTCOMES.map((o) => <col key={o} style={{ width: `${62 / OUTCOMES.length}%` }} />)}
            <col style={{ width: "12%" }} />
          </colgroup>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "6px 16px 8px 0", color: "var(--fg-3)", fontWeight: 500, fontSize: 12 }} />
              {OUTCOMES.map((o) => (
                <th
                  key={o}
                  style={{ textAlign: "center", padding: "6px 8px 8px", color: FG1, fontWeight: 600, fontSize: 12, cursor: "default" }}
                  onMouseMove={(e) => {
                    const rect = containerRef.current?.getBoundingClientRect();
                    if (!rect) return;
                    setHeaderTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, outcome: o });
                  }}
                  onMouseLeave={() => setHeaderTooltip(null)}
                >
                  {OUTCOME_LABELS[o]}
                </th>
              ))}
              <th style={{ textAlign: "right", padding: "6px 0 8px 4px", color: "var(--fg-3)", fontWeight: 500, fontSize: 12 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {CHANNEL_GROUPS.map((group) => {
              const agg = groupAggregates.find((g) => g.name === group.name);
              if (!agg || agg.total === 0) return null;
              const isExpanded = expandedGroups.has(group.name);
              return (
                <Fragment key={group.name}>
                  <tr style={{ background: "var(--bg-2, #f5f4f2)" }}>
                    <td style={{ padding: "3px 16px 3px 0", color: "var(--fg-1)", fontWeight: 600 }}>
                      <button
                        onClick={() => toggleGroup(group.name)}
                        style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 5, padding: 0, color: "inherit", fontWeight: "inherit", fontSize: "inherit" }}
                      >
                        <i className={`ti ti-chevron-${isExpanded ? "down" : "right"}`} style={{ fontSize: 11, color: FG3 }} />
                        {CHANNEL_ICONS[group.name] && <i className={`ti ${CHANNEL_ICONS[group.name]}`} style={{ fontSize: 13, color: FG3 }} />}
                        {group.name}
                      </button>
                    </td>
                    {OUTCOMES.map((o) => renderCell(group.name, o, agg.counts.get(o) ?? 0, agg.total))}
                    {renderTotalCell(agg.total, grandTotal)}
                  </tr>
                  {isExpanded && group.channels.map((ch) => {
                    const d = channelByName.get(ch);
                    if (!d || d.total === 0) return null;
                    return (
                      <tr key={ch} style={{ background: "var(--bg-1)" }}>
                        <td style={{ padding: "3px 16px 3px 20px", color: FG3, fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                            {CHANNEL_ICONS[ch] && <i className={`ti ${CHANNEL_ICONS[ch]}`} style={{ fontSize: 13 }} />}
                            {ch}
                          </span>
                        </td>
                        {OUTCOMES.map((o) => renderCell(ch, o, d.counts.get(o) ?? 0, d.total, true))}
                        {renderTotalCell(d.total, grandTotal, true)}
                      </tr>
                    );
                  })}
                </Fragment>
              );
            })}
            {standaloneChannels.map(({ attr, counts, total }) => (
              <tr key={attr}>
                <td style={{ padding: "3px 16px 3px 0", color: "var(--fg-2)", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                    {CHANNEL_ICONS[attr] && <i className={`ti ${CHANNEL_ICONS[attr]}`} style={{ fontSize: 13 }} />}
                    {attr}
                  </span>
                </td>
                {OUTCOMES.map((o) => renderCell(attr, o, counts.get(o) ?? 0, total))}
                {renderTotalCell(total, grandTotal)}
              </tr>
            ))}
          </tbody>
        </table>
        {tooltip && (
          <div style={{
            position: "absolute", left: tooltip.x + 14, top: tooltip.y - 10,
            background: "var(--bg-elevated, #fff)", border: "1px solid var(--border-default, #e0e0e0)",
            borderRadius: 7, padding: "7px 10px", fontSize: 12, pointerEvents: "none",
            whiteSpace: "nowrap", boxShadow: "0 2px 10px rgba(0,0,0,0.1)", color: FG1, zIndex: 10,
          }}>
            <div style={{ fontWeight: 600, marginBottom: 3 }}>
              {Math.round(tooltip.pct * 100)}% of {OUTCOME_LABELS[tooltip.outcome]} users came from {tooltip.channel}
            </div>
            <div style={{ color: MUTED }}>
              {tooltip.count.toLocaleString()} users · {OUTCOME_DESCRIPTIONS[tooltip.outcome]}
            </div>
          </div>
        )}
        {headerTooltip && (
          <div style={{
            position: "absolute", left: headerTooltip.x + 14, top: headerTooltip.y + 8,
            background: "var(--bg-elevated, #fff)", border: "1px solid var(--border-default, #e0e0e0)",
            borderRadius: 7, padding: "7px 10px", fontSize: 12, pointerEvents: "none",
            whiteSpace: "nowrap", boxShadow: "0 2px 10px rgba(0,0,0,0.1)", color: FG1, zIndex: 10,
          }}>
            <div>{OUTCOME_DESCRIPTIONS[headerTooltip.outcome]}</div>
          </div>
        )}
      </div>
    </div>
  );
}

export function Attributions() {
  const result = useAdminQuery(SQL);
  const wordResult = useAdminQuery(WORD_SQL);

  const [filters, setFilters] = useState<Filters>(() => ({
    months: [],
    countries: [],
    roles: [],
    companySizes: [],
    excludeSupport: true,
  }));

  const rows = useMemo<AttrRow[]>(() => {
    if (!result.data) return [];
    return mapAttrRows(result.data.columns, result.data.rows as unknown[][]);
  }, [result.data]);

  const availableCountries = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => set.add(groupCountry(r.country_code)));
    return Array.from(set);
  }, [rows]);

  const availableRoles = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => set.add(r.user_role ?? "unknown"));
    return Array.from(set).sort();
  }, [rows]);

  const filteredRows = useMemo(() => applyAttrFilters(rows, filters), [rows, filters]);

  const wordRows = useMemo<WordRow[]>(() => {
    if (!wordResult.data) return [];
    return mapWordRows(wordResult.data.columns, wordResult.data.rows as unknown[][]);
  }, [wordResult.data]);

  const filteredWordRows = useMemo(() => applyWordFilters(wordRows, filters), [wordRows, filters]);
  const usersInView = useMemo(() => filteredRows.reduce((s, r) => s + r.user_count, 0), [filteredRows]);
  const pagePills = useMemo(() => filtersToPills(filters), [filters]);

  const demoRows = useMemo(() => filteredRows.filter((r) => r.had_demo), [filteredRows]);
  const selfRows = useMemo(() => filteredRows.filter((r) => !r.had_demo), [filteredRows]);

  const demoTotal     = demoRows.reduce((s, r) => s + r.user_count, 0);
  const selfTotal     = selfRows.reduce((s, r) => s + r.user_count, 0);
  const demoConverted = demoRows.filter((r) => r.outcome === "Pro" || r.outcome === "Plus").reduce((s, r) => s + r.user_count, 0);
  const selfConverted = selfRows.filter((r) => r.outcome === "Pro" || r.outcome === "Plus").reduce((s, r) => s + r.user_count, 0);
  const demoRate      = demoTotal > 0 ? demoConverted / demoTotal : 0;
  const selfRate      = selfTotal > 0 ? selfConverted / selfTotal : 0;
  const ratio         = selfRate > 0 ? demoRate / selfRate : 0;

  return (
    <>
      <Topbar crumbs={["Dashboards", "Attributions"]} />
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Attributions</h1>
            <p className="lede">
              Where sign-ups come from and what they convert to, split by demo vs. self sign-up path.
            </p>
          </div>
        </div>

        <FiltersBar
          filters={filters}
          onChange={setFilters}
          availableCountries={availableCountries}
          availableRoles={availableRoles}
          usersInView={usersInView}
        />

        {result.isLoading ? (
          <LoadingPanel />
        ) : result.isError ? (
          <p style={{ color: "var(--fg-danger)" }}>{(result.error as Error).message}</p>
        ) : rows.length === 0 ? (
          <div className="empty">No attribution data found.</div>
        ) : filteredRows.length === 0 ? (
          <div className="empty">No data matches your filter combination. Try widening the filters.</div>
        ) : (
          <>
            <WordCloud
              texts={filteredWordRows.map((r) => r.attribution_raw)}
              title="Where did users hear about Marloo?"
              subtitle={`Raw attribution text, tokenized across ${filteredWordRows.length.toLocaleString()} users with a free-text response. Word size = frequency.`}
              defaultExcluded={["test", "prospecting", "abo"]}
              filterPills={pagePills}
            />
            <HeatmapCard rows={filteredRows} pagePills={pagePills} />
            <div className="card" style={{ marginTop: 16 }}>
              <SectionCardHeader
                title="Sign-up paths by channel"
                subtitle="Flow from sign-up channel (left) through demo or self sign-up path (centre) to outcome tier (right). Band width = user count."
                filterPills={pagePills}
              />
              <div className="card-body">
                {ratio > 0 && (
                  <div className="callout" style={{ marginBottom: 16 }}>
                    <i className="ti ti-trending-up" />
                    <span>
                      Demo cohort converts <strong>{ratio.toFixed(1)}×</strong> more often than self sign-up
                      ({(demoRate * 100).toFixed(1)}% vs {(selfRate * 100).toFixed(1)}%). {fmtN(usersInView)} users in view.
                    </span>
                  </div>
                )}
                <div className="grid-2">
                  <SankeyCard rows={demoRows} variant="demo" pagePills={pagePills} />
                  <SankeyCard rows={selfRows} variant="self" pagePills={pagePills} />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
